import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  Alert,
  Snackbar,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteIcon from '@mui/icons-material/Delete';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletIcon from '@mui/icons-material/Tablet';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
// import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublicIcon from '@mui/icons-material/Public';

// Contexts and Components
import { ContentCardSkeleton, PageHeaderCard } from '../../components/common';
import { ROLES, useAuth } from '../../contexts/AuthContext';
import { useThemeMode } from '../../contexts/ThemeModeContext';

// Services
import { authAPI, settingsAPI } from '../../services/api';

// Utils and Constants
import {
  DEFAULT_SETTINGS,
  DEFAULT_SMTP_SETTINGS,
  DAY_OPTIONS,
  HOUR_OPTIONS,
  TIMEZONE_OPTIONS,
  DATE_FORMAT_OPTIONS,
  EXPIRATION_OPTIONS,
} from '../../constants/settings';

// Mock shared links data
// const mockLiveLinks = [
//   {
//     id: 'l1',
//     name: 'Weekly Status Report',
//     url: 'https://statusreport.app/share/live/abc123',
//     createdAt: '2026-01-10T09:00:00',
//     lastAccessed: '2026-01-25T10:30:00',
//     accessCount: 45,
//     allowedUsers: ['john@company.com', 'sarah@company.com'],
//   },
//   {
//     id: 'l2',
//     name: 'Project Dashboard',
//     url: 'https://statusreport.app/share/live/def456',
//     createdAt: '2026-01-05T14:00:00',
//     lastAccessed: '2026-01-24T16:20:00',
//     accessCount: 128,
//     allowedUsers: ['team@company.com'],
//   },
// ];

// const mockTemporaryLinks = [
//   {
//     id: 't1',
//     name: 'Q4 Report for Client',
//     url: 'https://statusreport.app/share/temp/xyz789',
//     createdAt: '2026-01-20T11:00:00',
//     expiresAt: '2026-02-20T11:00:00',
//     accessCount: 12,
//     status: 'active',
//   },
//   {
//     id: 't2',
//     name: 'December Summary',
//     url: 'https://statusreport.app/share/temp/uvw321',
//     createdAt: '2026-01-01T09:00:00',
//     expiresAt: '2026-01-15T09:00:00',
//     accessCount: 8,
//     status: 'expired',
//   },
// ];

const SettingsSection = ({ title, children }) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      variant="subtitle1"
      fontWeight={600}
      sx={{
        mb: 2,
        pb: 1,
        borderBottom: '2px solid',
        borderColor: 'primary.main',
        display: 'inline-block',
      }}
    >
      {title}
    </Typography>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {children}
    </Box>
  </Box>
);

const SettingRow = ({ label, description, children }) => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 3,
    py: 1,
    flexDirection: { xs: 'column', sm: 'row' },
  }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.25 }}>
        {label}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    <Box sx={{ minWidth: { xs: '100%', sm: 200 }, maxWidth: { sm: 200 } }}>
      {children}
    </Box>
  </Box>
);

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { hasAnyRole } = useAuth();
  const { themePreference, setThemePreference } = useThemeMode();
  const canAccessSettings = hasAnyRole([ROLES.ADMIN, ROLES.COMPANY_ADMIN]);
  const isPlatformAdmin = hasAnyRole([ROLES.ADMIN]);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [smtpSettings, setSmtpSettings] = useState(DEFAULT_SMTP_SETTINGS);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSavingSmtpSettings, setIsSavingSmtpSettings] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Session management state
  const [sessionTab, setSessionTab] = useState(0);
  const [activeSessions, setActiveSessions] = useState([]);
  const [inactiveSessions, setInactiveSessions] = useState([]);
  const [killedSessions, setKilledSessions] = useState([]);
  const [killDialogOpen, setKillDialogOpen] = useState(false);
  const [sessionToKill, setSessionToKill] = useState(null);
  const [killReason, setKillReason] = useState('no_longer_used');
  const [isSessionsLoading, setIsSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');

  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [killAllSessions, setKillAllSessions] = useState(false);
  const [showSessionList, setShowSessionList] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  // Link sharing state
  // const [linkTab, setLinkTab] = useState(0);
  // const [liveLinks, setLiveLinks] = useState(mockLiveLinks);
  // const [temporaryLinks, setTemporaryLinks] = useState(mockTemporaryLinks);
  const [createLinkDialogOpen, setCreateLinkDialogOpen] = useState(false);
  const [newLinkType, setNewLinkType] = useState('live');

  const resetPasswordDialogState = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordErrors({});
    setKillAllSessions(false);
    setShowSessionList(false);
    setIsPasswordSubmitting(false);
  };

  const loadSessions = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsSessionsLoading(true);
    }
    setSessionsError('');

    try {
      const data = await authAPI.getSessions();
      setActiveSessions(data?.activeSessions || []);
      setInactiveSessions(data?.inactiveSessions || []);
      setKilledSessions(data?.killedSessions || []);
    } catch (error) {
      setSessionsError(error.message || 'Failed to load sessions');
      setActiveSessions([]);
      setInactiveSessions([]);
      setKilledSessions([]);
    } finally {
      if (!silent) {
        setIsSessionsLoading(false);
      }
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const response = await settingsAPI.get();
      const nextSettings = {
        ...DEFAULT_SETTINGS,
        ...(response || {}),
        // Keep UI theme select aligned with the currently applied global preference.
        theme: themePreference,
      };
      setSettings(nextSettings);
    } catch (error) {
      setSettings({
        ...DEFAULT_SETTINGS,
        theme: themePreference,
      });
      setSnackbarMessage(error.message || 'Failed to load settings. Using defaults.');
      setSnackbarOpen(true);
    }
  }, [themePreference]);

  const loadSmtpSettings = useCallback(async () => {
    if (!isPlatformAdmin) {
      setSmtpSettings(DEFAULT_SMTP_SETTINGS);
      return;
    }

    try {
      const response = await settingsAPI.getSmtp();
      setSmtpSettings({
        ...DEFAULT_SMTP_SETTINGS,
        ...(response || {}),
      });
    } catch (error) {
      setSmtpSettings(DEFAULT_SMTP_SETTINGS);
      setSnackbarMessage(error.message || 'Failed to load SMTP settings');
      setSnackbarOpen(true);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      await Promise.all([
        loadSettings(),
        loadSmtpSettings(),
        loadSessions(),
      ]);
      setIsLoading(false);
    };

    loadInitialData();
  }, [loadSessions, loadSettings, loadSmtpSettings]);

  const handleChange = (field, value) => {
    const nextSettings = { ...settings, [field]: value };
    setSettings(nextSettings);

    if (field === 'theme') {
      setThemePreference(value);

      settingsAPI.update(nextSettings).catch(() => {
        setSnackbarMessage('Theme changed locally. Click Save to sync it on server.');
        setSnackbarOpen(true);
      });
    }
  };

  const handleSave = async () => {
    setIsSavingSettings(true);
    try {
      const response = await settingsAPI.update(settings);
      const nextSettings = response || settings;
      setSettings(nextSettings);
      setThemePreference(nextSettings.theme);
      setSnackbarMessage('Settings saved successfully');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to save settings');
      setSnackbarOpen(true);
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSmtpChange = (field, value) => {
    setSmtpSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSmtp = async () => {
    setIsSavingSmtpSettings(true);
    try {
      const response = await settingsAPI.updateSmtp(smtpSettings);
      const nextSettings = response || smtpSettings;
      setSmtpSettings(nextSettings);
      setSnackbarMessage('SMTP settings saved successfully');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to save SMTP settings');
      setSnackbarOpen(true);
    } finally {
      setIsSavingSmtpSettings(false);
    }
  };

  const handleReset = async () => {
    setIsSavingSettings(true);
    try {
      const response = await settingsAPI.reset();
      const nextSettings = response || DEFAULT_SETTINGS;
      setSettings(nextSettings);
      setThemePreference(nextSettings.theme);
      setSnackbarMessage('Settings reset to defaults');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to reset settings');
      setSnackbarOpen(true);
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Session management handlers
  const handleOpenKillDialog = (session) => {
    setSessionToKill(session);
    setKillReason('no_longer_used');
    setKillDialogOpen(true);
  };

  const handleKillSession = async () => {
    if (!sessionToKill) return;

    try {
      const response = await authAPI.terminateSession(sessionToKill.id, killReason);
      setKillDialogOpen(false);
      setSessionToKill(null);
      await loadSessions({ silent: true });

      if (killReason === 'unknown_device') {
        setSnackbarMessage(response?.message || 'Session terminated. Consider changing your password.');
        setSnackbarOpen(true);
        setTimeout(() => {
          setPasswordDialogOpen(true);
        }, 350);
      } else {
        setSnackbarMessage(response?.message || 'Session terminated successfully');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to terminate session');
      setSnackbarOpen(true);
    }
  };

  const handleChangePassword = async () => {
    const nextErrors = {};

    if (!passwordForm.currentPassword) {
      nextErrors.currentPassword = 'Current password is required';
    }
    if (!passwordForm.newPassword) {
      nextErrors.newPassword = 'New password is required';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}/.test(passwordForm.newPassword)) {
      nextErrors.newPassword = 'Use 8+ chars with uppercase, lowercase, and number';
    }
    if (!passwordForm.confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsPasswordSubmitting(true);

    try {
      const response = await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
        signOutOtherDevices: killAllSessions,
      });

      setSnackbarMessage(response?.message || 'Password changed successfully');
      setSnackbarOpen(true);
      setPasswordDialogOpen(false);
      resetPasswordDialogState();
      await loadSessions({ silent: true });
    } catch (error) {
      setSnackbarMessage(error.message || 'Failed to change password');
      setSnackbarOpen(true);
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  // const handleCopyLink = (url) => {
  //   navigator.clipboard.writeText(url);
  //   setSnackbarMessage('Link copied to clipboard');
  //   setSnackbarOpen(true);
  // };

  // const handleDeleteLink = (linkId, isLive) => {
  //   if (isLive) {
  //     setLiveLinks((prev) => prev.filter((l) => l.id !== linkId));
  //   } else {
  //     setTemporaryLinks((prev) => prev.filter((l) => l.id !== linkId));
  //   }
  //   setSnackbarMessage('Link deleted');
  //   setSnackbarOpen(true);
  // };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <PhoneAndroidIcon fontSize="small" />;
      case 'tablet':
        return <TabletIcon fontSize="small" />;
      default:
        return <ComputerIcon fontSize="small" />;
    }
  };

  // Session Table Component
  const SessionTable = ({ sessions, type }) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Device</TableCell>
            {!isMobile && <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Browser</TableCell>}
            {!isTablet && <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>IP Address</TableCell>}
            {!isTablet && <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Location</TableCell>}
            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              {type === 'active' ? 'Last Activity' : type === 'inactive' ? 'Ended' : 'Killed'}
            </TableCell>
            {type === 'active' && <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>}
            {(type === 'inactive' || type === 'killed') && <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Reason</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.length === 0 ? (
            <TableRow>
              <TableCell colSpan={type === 'active' ? 6 : 6} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary" variant="body2">
                  No {type} sessions
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sessions.map((session) => (
              <TableRow key={session.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getDeviceIcon(session.deviceType)}
                    <Box>
                      <Typography
                        component="div"
                        variant="body2"
                        fontWeight={500}
                        sx={{
                          fontSize: '0.8rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        {session.device}
                        {session.isCurrent && (
                          <Chip label="Current" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                {!isMobile && (
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{session.browser}</Typography>
                  </TableCell>
                )}
                {!isTablet && (
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                      {session.ipAddress}
                    </Typography>
                  </TableCell>
                )}
                {!isTablet && (
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {session.city}, {session.country}
                    </Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {formatDateTime(
                      type === 'active' ? session.lastActivity :
                        type === 'inactive' ? session.endTime : session.killedTime
                    )}
                  </Typography>
                </TableCell>
                {type === 'active' && (
                  <TableCell align="center">
                    {!session.isCurrent && (
                      <Tooltip title="Terminate Session" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenKillDialog(session)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
                {(type === 'inactive' || type === 'killed') && (
                  <TableCell>
                    <Chip
                      label={session.reason}
                      size="small"
                      color={session.reason === 'Unknown device' ? 'error' : 'default'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (!canAccessSettings) {
    return <Alert severity="error">You do not have permission to access settings.</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <PageHeaderCard
        title="Settings"
        subtitle="Manage your application preferences"
        titleVariant={isMobile ? 'h5' : 'h4'}
        titleFontWeight={700}
        actions={(
          <Box sx={{ display: 'flex', gap: 1.5, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<RestoreIcon />}
              onClick={handleReset}
              disabled={isSavingSettings}
              sx={{ borderColor: 'grey.300', color: 'text.secondary' }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isSavingSettings}
            >
              {isSavingSettings ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        )}
      />

      {/* Main Content */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          {isLoading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
              <Box>
                <ContentCardSkeleton lines={8} />
              </Box>
              <Box>
                <ContentCardSkeleton lines={8} />
              </Box>
            </Box>
          ) : (
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <SettingsSection title="General">
                  <SettingRow label="Theme" description="Application color scheme">
                    <FormControl fullWidth size="small">
                      <Select
                        value={settings.theme}
                        onChange={(e) => handleChange('theme', e.target.value)}
                      >
                        <MenuItem value="light">Light</MenuItem>
                        <MenuItem value="dark">Dark</MenuItem>
                        <MenuItem value="system">System</MenuItem>
                      </Select>
                    </FormControl>
                  </SettingRow>

                  <SettingRow label="Date Format" description="How dates are displayed">
                    <FormControl fullWidth size="small">
                      <Select
                        value={settings.dateFormat}
                        onChange={(e) => handleChange('dateFormat', e.target.value)}
                      >
                        {DATE_FORMAT_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SettingRow>

                  <SettingRow label="Timezone" description="Your local timezone">
                    <FormControl fullWidth size="small">
                      <Select
                        value={settings.timezone}
                        onChange={(e) => handleChange('timezone', e.target.value)}
                      >
                        {TIMEZONE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SettingRow>
                </SettingsSection>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <SettingsSection title="Notifications">
                  <SettingRow label="Email Notifications" description="Receive email updates">
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                    />
                  </SettingRow>

                  <SettingRow label="Deadline Reminders" description="Get reminded before deadlines">
                    <Switch
                      checked={settings.reportReminders}
                      onChange={(e) => handleChange('reportReminders', e.target.checked)}
                      disabled={!settings.emailNotifications}
                    />
                  </SettingRow>

                  <SettingRow label="Reminder Timing" description="Hours before deadline">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={settings.reminderHoursBefore}
                      onChange={(e) => handleChange('reminderHoursBefore', parseInt(e.target.value) || 0)}
                      disabled={!settings.emailNotifications || !settings.reportReminders}
                      inputProps={{ min: 1, max: 168 }}
                    />
                  </SettingRow>
                </SettingsSection>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <SettingsSection title="Reports">
                  <SettingRow label="Deadline Day" description="Weekly report submission day">
                    <FormControl fullWidth size="small">
                      <Select
                        value={settings.reportDeadlineDay}
                        onChange={(e) => handleChange('reportDeadlineDay', e.target.value)}
                      >
                        {DAY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SettingRow>

                  <SettingRow label="Deadline Hour" description="Submission cutoff time">
                    <FormControl fullWidth size="small">
                      <Select
                        value={settings.reportDeadlineHour}
                        onChange={(e) => handleChange('reportDeadlineHour', e.target.value)}
                      >
                        {HOUR_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SettingRow>

                  <SettingRow label="Auto-publish" description="Publish reports at deadline">
                    <Switch
                      checked={settings.autoPublishReports}
                      onChange={(e) => handleChange('autoPublishReports', e.target.checked)}
                    />
                  </SettingRow>
                </SettingsSection>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <SettingsSection title="Sharing">
                  <SettingRow label="Link Expiration" description="Default share link validity">
                    <FormControl fullWidth size="small">
                      <Select
                        value={settings.defaultShareExpiration}
                        onChange={(e) => handleChange('defaultShareExpiration', e.target.value)}
                      >
                        {EXPIRATION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </SettingRow>

                  <SettingRow label="Public Links" description="Allow unauthenticated access">
                    <Switch
                      checked={settings.allowPublicSharing}
                      onChange={(e) => handleChange('allowPublicSharing', e.target.checked)}
                    />
                  </SettingRow>
                </SettingsSection>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <SettingsSection title="Security">
                  <SettingRow label="Session Timeout" description="Auto-logout after inactivity (min)">
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={settings.sessionTimeout}
                      onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 5, max: 480 }}
                    />
                  </SettingRow>

                  <SettingRow label="Remember Me" description="Stay logged in option">
                    <Switch
                      checked={settings.rememberMe}
                      onChange={(e) => handleChange('rememberMe', e.target.checked)}
                    />
                  </SettingRow>

                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="outlined"
                      size={isMobile ? 'small' : 'medium'}
                      startIcon={<LockIcon />}
                      onClick={() => setPasswordDialogOpen(true)}
                      sx={{ borderColor: 'grey.300' }}
                    >
                      Change Password
                    </Button>
                  </Box>
                </SettingsSection>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <SettingsSection title="System Information">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[
                      { label: 'Application Version', value: '2.0.0' },
                      { label: 'Environment', value: 'Development' },
                      { label: 'API Status', value: 'Connected', color: 'success.main' },
                      {
                        label: 'Last Sync', value: new Date().toLocaleString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })
                      },
                    ].map((item) => (
                      <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ color: item.color || 'text.primary' }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </SettingsSection>
              </Grid>

              {isPlatformAdmin && (
                <Grid size={{ xs: 12 }}>
                  <SettingsSection title="SMTP Configuration (Admin)">
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="SMTP Host" description="Mail server host name">
                          <TextField
                            fullWidth
                            size="small"
                            value={smtpSettings.smtpHost}
                            onChange={(e) => handleSmtpChange('smtpHost', e.target.value)}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="SMTP Port" description="Mail server port">
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={smtpSettings.smtpPort}
                            onChange={(e) => handleSmtpChange('smtpPort', parseInt(e.target.value, 10) || 0)}
                            inputProps={{ min: 1, max: 65535 }}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="SMTP Username" description="SMTP auth username">
                          <TextField
                            fullWidth
                            size="small"
                            value={smtpSettings.smtpUsername}
                            onChange={(e) => handleSmtpChange('smtpUsername', e.target.value)}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="SMTP Password" description="SMTP auth password">
                          <TextField
                            fullWidth
                            size="small"
                            type="password"
                            value={smtpSettings.smtpPassword}
                            onChange={(e) => handleSmtpChange('smtpPassword', e.target.value)}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="From Email" description="Sender email address">
                          <TextField
                            fullWidth
                            size="small"
                            value={smtpSettings.smtpFromEmail}
                            onChange={(e) => handleSmtpChange('smtpFromEmail', e.target.value)}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="From Name" description="Sender display name">
                          <TextField
                            fullWidth
                            size="small"
                            value={smtpSettings.smtpFromName}
                            onChange={(e) => handleSmtpChange('smtpFromName', e.target.value)}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="Use TLS" description="Enable STARTTLS encryption">
                          <Switch
                            checked={smtpSettings.smtpUseTls}
                            onChange={(e) => handleSmtpChange('smtpUseTls', e.target.checked)}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="Use SSL" description="Enable implicit SSL/TLS">
                          <Switch
                            checked={smtpSettings.smtpUseSsl}
                            onChange={(e) => handleSmtpChange('smtpUseSsl', e.target.checked)}
                          />
                        </SettingRow>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <SettingRow label="Reply-To" description="Optional reply-to address">
                          <TextField
                            fullWidth
                            size="small"
                            value={smtpSettings.smtpReplyTo}
                            onChange={(e) => handleSmtpChange('smtpReplyTo', e.target.value)}
                          />
                        </SettingRow>
                      </Grid>
                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                      <Button
                        variant="contained"
                        size={isMobile ? 'small' : 'medium'}
                        startIcon={<SaveIcon />}
                        onClick={handleSaveSmtp}
                        disabled={isSavingSmtpSettings}
                      >
                        {isSavingSmtpSettings ? 'Saving...' : 'Save SMTP'}
                      </Button>
                    </Box>
                  </SettingsSection>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Session Management Section */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 8,
                height: 24,
                borderRadius: 1,
                backgroundColor: '#d32f2f',
              }} />
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                Session Management
              </Typography>
            </Box>
            <Tooltip title="Refresh" arrow>
              <IconButton size="small" onClick={() => loadSessions()} disabled={isSessionsLoading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {sessionsError && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>
              {sessionsError}
            </Alert>
          )}

          <Tabs
            value={sessionTab}
            onChange={(e, v) => setSessionTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Active Sessions
                  <Chip label={activeSessions.length} size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                </Box>
              }
              sx={{ textTransform: 'none', fontSize: '0.85rem' }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Inactive
                  <Chip label={inactiveSessions.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                </Box>
              }
              sx={{ textTransform: 'none', fontSize: '0.85rem' }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Killed
                  <Chip label={killedSessions.length} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                </Box>
              }
              sx={{ textTransform: 'none', fontSize: '0.85rem' }}
            />
          </Tabs>

          {sessionTab === 0 && <SessionTable sessions={activeSessions} type="active" />}
          {sessionTab === 1 && (
            <>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                Inactive sessions are those that timed out naturally due to inactivity.
              </Alert>
              <SessionTable sessions={inactiveSessions} type="inactive" />
            </>
          )}
          {sessionTab === 2 && (
            <>
              <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
                These sessions were explicitly terminated by you or the system for security reasons.
              </Alert>
              <SessionTable sessions={killedSessions} type="killed" />
            </>
          )}
        </CardContent>
      </Card>

      {/* Link Sharing Section */}
      {/* <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: '#fff',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 8,
                height: 24,
                borderRadius: 1,
                backgroundColor: '#1976d2',
              }} />
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                Link Sharing
              </Typography>
            </Box>
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<AddIcon />}
              onClick={() => setCreateLinkDialogOpen(true)}
            >
              Create Link
            </Button>
          </Box>

          <Tabs
            value={linkTab}
            onChange={(e, v) => setLinkTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PublicIcon sx={{ fontSize: 18 }} />
                  Live Links
                  <Chip label={liveLinks.length} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                </Box>
              }
              sx={{ textTransform: 'none', fontSize: '0.85rem' }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon sx={{ fontSize: 18 }} />
                  Temporary Links
                  <Chip label={temporaryLinks.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                </Box>
              }
              sx={{ textTransform: 'none', fontSize: '0.85rem' }}
            />
          </Tabs>

          {linkTab === 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                Live links always show the latest report. When a new report is published, the same link will display the updated content.
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Name</TableCell>
                      {!isMobile && <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>URL</TableCell>}
                      {!isTablet && <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Last Accessed</TableCell>}
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Views</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {liveLinks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary" variant="body2">
                            No live links created
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      liveLinks.map((link) => (
                        <TableRow key={link.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                              <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                                {link.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                                {link.url.replace('https://statusreport.app/', '.../')}
                              </Typography>
                            </TableCell>
                          )}
                          {!isTablet && (
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {formatDateTime(link.lastAccessed)}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Chip label={link.accessCount} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Copy Link" arrow>
                              <IconButton size="small" onClick={() => handleCopyLink(link.url)} sx={{ color: 'primary.main' }}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                              <IconButton size="small" onClick={() => handleDeleteLink(link.id, true)} sx={{ color: 'error.main' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {linkTab === 1 && (
            <>
              <Alert severity="warning" sx={{ mb: 2, fontSize: '0.8rem' }}>
                Temporary links expire after a set period and cannot be used once expired.
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Name</TableCell>
                      {!isMobile && <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>URL</TableCell>}
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Expires</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {temporaryLinks.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary" variant="body2">
                            No temporary links created
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      temporaryLinks.map((link) => (
                        <TableRow key={link.id} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTimeIcon sx={{ fontSize: 18, color: link.status === 'expired' ? 'text.disabled' : 'warning.main' }} />
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ fontSize: '0.85rem', color: link.status === 'expired' ? 'text.disabled' : 'text.primary' }}
                              >
                                {link.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', fontFamily: 'monospace', color: 'text.secondary' }}>
                                {link.url.replace('https://statusreport.app/', '.../')}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {formatDateTime(link.expiresAt)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={link.status === 'active' ? 'Active' : 'Expired'}
                              size="small"
                              color={link.status === 'active' ? 'success' : 'default'}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Copy Link" arrow>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyLink(link.url)}
                                  sx={{ color: 'primary.main' }}
                                  disabled={link.status === 'expired'}
                                >
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete" arrow>
                              <IconButton size="small" onClick={() => handleDeleteLink(link.id, false)} sx={{ color: 'error.main' }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card> */}

      {/* Kill Session Dialog */}
      <Dialog open={killDialogOpen} onClose={() => setKillDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6" color="inherit">Terminate Session</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {sessionToKill && (
            <Box sx={{ pt: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  {getDeviceIcon(sessionToKill.deviceType)}
                  <Typography fontWeight={600}>{sessionToKill.device}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {sessionToKill.browser} - {sessionToKill.ipAddress}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sessionToKill.city}, {sessionToKill.country}
                </Typography>
              </Paper>

              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Why are you terminating this session?
              </Typography>

              <RadioGroup
                value={killReason}
                onChange={(e) => setKillReason(e.target.value)}
              >
                <FormControlLabel
                  value="no_longer_used"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500}>Device no longer in use</Typography>
                      <Typography variant="caption" color="text.secondary">
                        This is a known device that I no longer need access from
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="unknown_device"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={500} color="error.main">Unknown device</Typography>
                      <Typography variant="caption" color="text.secondary">
                        I do not recognize this device - my account may be compromised
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>

              {killReason === 'unknown_device' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  If you do not recognize this device, we strongly recommend changing your password immediately after terminating this session.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setKillDialogOpen(false)} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleKillSession} variant="contained" color="error">
            Terminate Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon color="inherit" />
            <Typography variant="h6" color="inherit">Change Password</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Current Password"
              type="password"
              size="small"
              value={passwordForm.currentPassword}
              onChange={(e) => {
                setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }));
                if (passwordErrors.currentPassword) {
                  setPasswordErrors((prev) => ({ ...prev, currentPassword: '' }));
                }
              }}
              error={Boolean(passwordErrors.currentPassword)}
              helperText={passwordErrors.currentPassword || ''}
            />
            <TextField
              fullWidth
              label="New Password"
              type="password"
              size="small"
              value={passwordForm.newPassword}
              onChange={(e) => {
                setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }));
                if (passwordErrors.newPassword) {
                  setPasswordErrors((prev) => ({ ...prev, newPassword: '' }));
                }
              }}
              error={Boolean(passwordErrors.newPassword)}
              helperText={passwordErrors.newPassword || 'Must be at least 8 characters with uppercase, lowercase, and number'}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type="password"
              size="small"
              value={passwordForm.confirmPassword}
              onChange={(e) => {
                setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }));
                if (passwordErrors.confirmPassword) {
                  setPasswordErrors((prev) => ({ ...prev, confirmPassword: '' }));
                }
              }}
              error={Boolean(passwordErrors.confirmPassword)}
              helperText={passwordErrors.confirmPassword || ''}
            />

            <Divider />

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={killAllSessions}
                    onChange={(e) => {
                      setKillAllSessions(e.target.checked);
                      setShowSessionList(e.target.checked);
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      Sign out of all other devices
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Recommended for security if your account was compromised
                    </Typography>
                  </Box>
                }
              />

              {showSessionList && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The following devices will be signed out and will need to sign in again:
                  </Alert>
                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 200,
                      overflow: 'auto',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      {activeSessions.filter((s) => !s.isCurrent).length === 0 ? (
                        <Typography variant="body2" color="text.secondary" align="center">
                          No other active sessions
                        </Typography>
                      ) : (
                        activeSessions
                          .filter((s) => !s.isCurrent)
                          .map((session, index) => (
                            <Box
                              key={session.id}
                              sx={{
                                py: 1,
                                borderBottom: index < activeSessions.filter((s) => !s.isCurrent).length - 1 ? '1px solid' : 'none',
                                borderColor: 'divider',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getDeviceIcon(session.deviceType)}
                                <Typography variant="body2" fontWeight={500}>{session.device}</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {session.browser} - {session.city}, {session.country}
                              </Typography>
                            </Box>
                          ))
                      )}
                    </Box>
                  </Paper>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => {
            setPasswordDialogOpen(false);
            resetPasswordDialogState();
          }} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button onClick={handleChangePassword} variant="contained" disabled={isPasswordSubmitting}>
            {isPasswordSubmitting ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Link Dialog */}
      <Dialog open={createLinkDialogOpen} onClose={() => setCreateLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon color="inherit" />
            <Typography variant="h6" color="inherit">Create Share Link</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Link Name"
              size="small"
              placeholder="e.g., Weekly Report for Client"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Link Type</InputLabel>
              <Select
                value={newLinkType}
                label="Link Type"
                onChange={(e) => setNewLinkType(e.target.value)}
              >
                <MenuItem value="live">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon sx={{ fontSize: 18 }} />
                    Live Link - Always shows latest report
                  </Box>
                </MenuItem>
                <MenuItem value="temporary">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 18 }} />
                    Temporary Link - Expires after set time
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {newLinkType === 'live' && (
              <Alert severity="info" icon={<PublicIcon />}>
                Live links always display the most recent report. When you publish a new report, users accessing this link will automatically see the updated content without needing a new URL.
              </Alert>
            )}

            {newLinkType === 'temporary' && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Expires In</InputLabel>
                  <Select label="Expires In" defaultValue={7}>
                    {EXPIRATION_OPTIONS.filter((o) => o.value > 0).map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Alert severity="warning" icon={<AccessTimeIcon />}>
                  Temporary links become invalid after the expiration date and cannot be reactivated.
                </Alert>
              </>
            )}

            <FormControl fullWidth size="small">
              <InputLabel>Report Selection</InputLabel>
              <Select label="Report Selection" defaultValue="latest">
                <MenuItem value="latest">Latest Report</MenuItem>
                <MenuItem value="specific">Specific Report (Select)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Allowed Email Addresses (Optional)"
              size="small"
              placeholder="email@company.com, team@company.com"
              helperText="Leave empty for public access. Separate multiple emails with commas."
            />
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setCreateLinkDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" startIcon={<CheckCircleIcon />}>
            Create Link
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
